import { useMemo } from 'react';
import {
  useGetStudySitesOptionsQuery,
  useGetStudyModalitiesOptionsQuery,
  useGetPriorityOptionsQuery,
} from '@/state/slices/options';
import {
  useGetReferringPhysiciansQuery,
  useGetSonographersQuery,
} from '@/state/slices/reports';
import { useGetPhysicianOptionsQuery } from '@/state/slices/users';

interface SelectOption {
  value: string;
  label: string;
}

export interface AdminStudyFilterOptions {
  siteOptions: SelectOption[];
  modalityOptions: SelectOption[];
  priorityOptions: SelectOption[];
  referringOptions: SelectOption[];
  physicianOptions: SelectOption[];
  sonographerOptions: SelectOption[];
}

export function useAdminStudyFilterOptions(): AdminStudyFilterOptions {
  const { data: sites } = useGetStudySitesOptionsQuery();
  const { data: modalities } = useGetStudyModalitiesOptionsQuery();
  const { data: priorities } = useGetPriorityOptionsQuery();
  const { data: referringData } = useGetReferringPhysiciansQuery();
  const { data: sonographerData } = useGetSonographersQuery();
  const { data: physicianData } = useGetPhysicianOptionsQuery();

  const referringOptions = useMemo(
    () =>
      referringData?.results.map((p) => ({
        value: p.uuid,
        label: `${p.firstName} ${p.lastName}`,
      })) ?? [],
    [referringData]
  );

  const sonographerOptions = useMemo(
    () =>
      sonographerData?.results.map((p) => ({
        value: p.uuid,
        label: `${p.firstName} ${p.lastName}`,
      })) ?? [],
    [sonographerData]
  );

  const physicianOptions = useMemo(
    () =>
      physicianData?.results.map((p) => ({
        value: p.uuid,
        label: `${p.firstName} ${p.lastName}`,
      })) ?? [],
    [physicianData]
  );

  const priorityOptions = useMemo(
    () => priorities?.map((p) => ({ value: p, label: p })) ?? [],
    [priorities]
  );

  const siteOptions = useMemo(
    () => sites?.map((s) => ({ value: s, label: s })) ?? [],
    [sites]
  );

  const modalityOptions = useMemo(
    () => modalities?.map((m) => ({ value: m, label: m })) ?? [],
    [modalities]
  );

  return {
    siteOptions,
    modalityOptions,
    priorityOptions,
    referringOptions,
    physicianOptions,
    sonographerOptions,
  };
}
